import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Drawer, Form, Input, Select, Checkbox } from "antd";
import { Error, Success } from "../../../commons/Messages/Messages";
import { modulesArrayForPermissions } from "../../../commons/modulesForPerm";
import { isAuthenticated } from "../../../commons/Auth/auth";
import DeleteModal from "../../../commons/DeleteModal/DeleteModal";
import UpdateRole from "./UpdateRole";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import AdminDefaultRoleManagement from "./DefaultRoles";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import PageToolbar from "../../../design-system/PageToolbar";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./RolesManagement.css";

import { useTranslation } from "react-i18next";
const AdminRoleManagement = () => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedRoleObject, setSelectedRoleObject] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [form] = Form.useForm();

  const fetchRoles = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/roles/all`,
        { serviceProviderId: isAuthenticated()?.serviceProviderId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setRoles(res.data.data);
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/employees/limited`,
        { pageSize: 100000, pageNo: 0 },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setEmployees(
        res?.data?.data?.map(emp => ({
          value: emp?._id,
          label: emp?.firstName + " " + emp?.lastName
        }))
      );
    } catch (err) {
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const handleRoleSelect = roleId => {
    const role = roles?.find(r => r?._id === roleId);
    setSelectedRoleObject(role);
    setSelectedRole(roleId);

    const permissionsObject = {};
    if (role?.permissions?.length > 0) {
      const permissionData = role?.permissions[0];
      Object.keys(permissionData).forEach(moduleName => {
        permissionsObject[moduleName] = permissionData[moduleName];
      });
    }

    setPermissions(permissionsObject);
  };

  const handlePermissionChange = (module, checkedValues) => {
    setPermissions(prev => ({
      ...prev,
      [module]: checkedValues
    }));
  };

  const handleSavePermissions = async () => {
    try {
      await axios
        .put(
          `${process.env.REACT_APP_BACKEND_URL}/roles/permissions`,
          { roleId: selectedRole, permissions },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        )
        .then(res => {
          if (res.status === 200) {
            Success(t("Permissions updated successfully!"));
            fetchRoles();
          } else {
            Error(res.data.errorMessage);
          }
        });
    } catch (err) {
      Error(err.response?.data?.error || t("Failed to update permissions."));
    }
  };

  const handleAddRole = async values => {
    try {
      await axios
        .post(
          `${process.env.REACT_APP_BACKEND_URL}/roles/create`,
          {
            serviceProviderId: isAuthenticated()?.serviceProviderId,
            title: values?.title,
            employees: values?.employees
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
        )
        .then(res => {
          if (res.status === 200) {
            Success(t("Role added successfully!"));
            setDrawerVisible(false);
            fetchRoles();
            form.resetFields();
          } else {
            Error(res.data.errorMessage);
          }
        });
    } catch (err) {
      Error(err.response?.data?.error);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchEmployees();
  }, []);

  const handleDeleteRole = async () => {
    try {
      await axios
        .delete(`${process.env.REACT_APP_BACKEND_URL}/roles/delete/${selectedRole}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        })
        .then(res => {
          if (res.status === 200) {
            Success(t("Role deleted successfully!"));
            fetchRoles();
          } else {
            Error(res.data.errorMessage);
          }
        });
    } catch (err) {
      Error(err.response?.data?.error || t("Failed to delete role."));
    }
  };

  return (
    <div className="roles-page">
      <section className="roles-page__section">
        <AdminDefaultRoleManagement />
      </section>
      <section className="roles-page__section">
        <h2 className="roles-page__heading">{t("Roles Management")}</h2>
        <PageToolbar
          filters={
            <Select
              placeholder={t("Select Role")}
              aria-label={t("Select Role")}
              style={{ width: "100%" }}
              onChange={handleRoleSelect}
              options={roles?.map(role => ({ value: role?._id, label: role?.title }))}
            />
          }
          actions={
            <>
              {selectedRole && (
                <>
                  <UpdateRole
                    updateParent={fetchRoles}
                    data={selectedRoleObject}
                    employees={employees}
                  />
                  <DeleteModal
                    disabled={selectedRoleObject?.employees?.length > 0 && t("Employees present")}
                    text="Role"
                    onClick={handleDeleteRole}
                  />
                </>
              )}
              <MainButtonComp
                text="Add Role"
                addBtn
                onClick={() => {
                  setDrawerVisible(true);
                }}
              />
            </>
          }
        />

        {selectedRole && roles?.length > 0 && (
          <>
            <ResponsiveTable
              dataSource={modulesArrayForPermissions?.map(module => ({
                key: module.name,
                module: module.name,
                permissions: (
                  <Checkbox.Group
                    className="roles-page__perms"
                    options={["Create", "Read", "Update", "Delete"].map(p => ({
                      value: p,
                      label: t(p)
                    }))}
                    value={permissions[module.name] || []}
                    onChange={checkedValues => handlePermissionChange(module.name, checkedValues)}
                  />
                )
              }))}
              columns={[
                { title: t("Module"), dataIndex: "module", mobile: "title" },
                { title: t("Permissions"), dataIndex: "permissions" }
              ]}
              pagination={false}
            />
            <div className="roles-page__save">
              <Button type="primary" onClick={handleSavePermissions}>
                {t("Save Permissions")}
              </Button>
            </div>
          </>
        )}

        <Drawer
          destroyOnClose
          title={t("Create Role")}
          open={drawerVisible}
          onClose={() => setDrawerVisible(false)}
          width={isMobile ? "100%" : 400}
          footer={
            <Button type="primary" htmlType="submit" form="admin-create-role" block>
              {t("Submit")}
            </Button>
          }
        >
          <Form id="admin-create-role" form={form} onFinish={handleAddRole} layout="vertical">
            <Form.Item
              name="title"
              label={t("Role Title")}
              rules={[{ required: true, message: t("Role title is required") }]}
            >
              <Input autoComplete="off" />
            </Form.Item>
            <Form.Item name="employees" label={t("Assign Employees")}>
              <Select mode="multiple" options={employees} />
            </Form.Item>
          </Form>
        </Drawer>
      </section>
    </div>
  );
};

export default AdminRoleManagement;
